export type FormatDateGranularity = 'date' | 'time' | 'datetime'

export type FormatDateOptions = {
  locale?: string
  timeZone?: string
  hour12?: boolean
  serverDateIsUtc?: boolean
  format?: FormatDateGranularity
}

export type FormatDateParts = {
  date: string
  time: string
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const hasTimeZoneInfo = (date: string): boolean => {
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(date)
}

export const getDateLocale = (locale?: string): string => {
  return locale || navigator?.language || 'es-CO'
}

export const getUserTimeZone = (timeZone?: string): string | undefined => {
  return timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const parseServerDate = (
  value: unknown,
  serverDateIsUtc = true,
): Date => {
  if (!value) return new Date(NaN)

  if (value instanceof Date) {
    return value
  }

  if (typeof value === 'number') {
    return new Date(value)
  }

  if (typeof value !== 'string') {
    return new Date(NaN)
  }

  const dateValue = value.trim()

  if (!dateValue) return new Date(NaN)

  if (hasTimeZoneInfo(dateValue)) {
    return new Date(dateValue)
  }

  if (!serverDateIsUtc) {
    return new Date(dateValue)
  }

  return new Date(`${dateValue}Z`)
}

export const formatDate = (
  value: unknown,
  options: FormatDateOptions = {},
): string => {
  const {
    locale,
    timeZone,
    hour12 = true,
    serverDateIsUtc = true,
    format = 'datetime',
  } = options

  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value.trim()) && format !== 'time') {
    const [year, month, day] = value.trim().split('-')
    return `${day}/${month}/${year}`
  }

  const date = parseServerDate(value, serverDateIsUtc)

  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  const dateTimeOptions: Intl.DateTimeFormatOptions = {
    hour12,
    timeZone: getUserTimeZone(timeZone),
  }

  if (format !== 'time') {
    dateTimeOptions.day = '2-digit'
    dateTimeOptions.month = '2-digit'
    dateTimeOptions.year = 'numeric'
  }

  if (format !== 'date') {
    dateTimeOptions.hour = '2-digit'
    dateTimeOptions.minute = '2-digit'
  }

  return new Intl.DateTimeFormat(getDateLocale(locale), dateTimeOptions).format(date)
}

export const formatDateParts = (
  value: unknown,
  options: FormatDateOptions = {},
): FormatDateParts => {
  const {
    locale,
    timeZone,
    serverDateIsUtc = true,
  } = options

  const date = parseServerDate(value, serverDateIsUtc)

  if (Number.isNaN(date.getTime())) {
    return { date: '-', time: '' }
  }

  const resolvedLocale = getDateLocale(locale)
  const resolvedTimeZone = getUserTimeZone(timeZone)

  return {
    date: new Intl.DateTimeFormat(resolvedLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: resolvedTimeZone,
    }).format(date),

    time: new Intl.DateTimeFormat(resolvedLocale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: resolvedTimeZone,
    }).format(date),
  }
}
